import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ALL_IMAGES } from "@/data/ships";
import useReveal from "@/hooks/useReveal";
import useSwipe from "@/hooks/useSwipe";

// Bento span pattern — cycled across all images for visual rhythm
const SPAN_CYCLE = [
  "md:col-span-8 md:row-span-2",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4 md:row-span-2",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-6",
  "md:col-span-6",
  "md:col-span-4",
  "md:col-span-8",
  "md:col-span-6 md:row-span-2",
  "md:col-span-6",
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Gallery() {
  const ref = useReveal();
  const [filter, setFilter] = useState("all");
  const [openIdx, setOpenIdx] = useState(null);
  const swipeRef = useRef(null);

  const ships = useMemo(() => {
    const seen = new Map();
    for (const img of ALL_IMAGES)
      seen.set(img.ship, (seen.get(img.ship) || 0) + 1);
    return Array.from(seen.entries());
  }, []);
  const dadCount = useMemo(
    () => ALL_IMAGES.filter((i) => i.isDad).length,
    []
  );

  const images = useMemo(() => {
    if (filter === "all") return ALL_IMAGES;
    if (filter === "dad") return ALL_IMAGES.filter((i) => i.isDad);
    return ALL_IMAGES.filter((i) => i.ship === filter);
  }, [filter]);

  const open = openIdx !== null ? images[openIdx] : null;

  const go = (delta) => {
    setOpenIdx((i) =>
      i === null ? null : (i + delta + images.length) % images.length
    );
  };

  useSwipe(swipeRef, () => go(-1), () => go(1));

  // Keyboard arrows on desktop
  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (f) => {
    setFilter(f);
    setOpenIdx(null);
  };

  return (
    <section
      id="gallery"
      ref={ref}
      className="reveal relative z-10 py-16 sm:py-24 md:py-32 border-t border-[var(--c-border)] scroll-mt-16 sm:scroll-mt-20"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
        <header className="max-w-3xl mb-8 sm:mb-12 md:mb-14">
          <p className="overline mb-3 sm:mb-4">Photographs</p>
          <h2 className="font-serif-display text-3xl xs:text-4xl md:text-5xl text-[var(--c-text)] tracking-tight">
            The Naval <span className="italic text-[var(--c-accent)]">Archives</span>
          </h2>
          <div className="hairline my-6 sm:my-8 max-w-xs" />
          <p className="text-[var(--c-text-muted)] text-sm sm:text-base md:text-lg leading-relaxed">
            {ALL_IMAGES.length} archival photographs of the actual ships and
            establishments you served in, Dad. Sourced from the Imperial War
            Museum and Wikimedia Commons. Tap any frame to view in full.
          </p>
        </header>

        {/* Filter by ship — makes 35 photographs easy to browse */}
        <div
          className="flex flex-wrap gap-2 mb-8 sm:mb-10"
          data-testid="gallery-filters"
        >
          <FilterChip
            active={filter === "all"}
            onClick={() => pick("all")}
            label="All"
            count={ALL_IMAGES.length}
            testid="gallery-filter-all"
          />
          <FilterChip
            active={filter === "dad"}
            onClick={() => pick("dad")}
            label="Dad's Own"
            count={dadCount}
            gold
            testid="gallery-filter-dad"
          />
          {ships.map(([name, count]) => (
            <FilterChip
              key={name}
              active={filter === name}
              onClick={() => pick(name)}
              label={name}
              count={count}
              testid={`gallery-filter-${slugify(name)}`}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-12 gap-3 md:gap-5 auto-rows-[180px] xs:auto-rows-[160px] md:auto-rows-[200px]">
          {images.map((img, i) => (
            <button
              key={filter + img.src + i}
              onClick={() => setOpenIdx(i)}
              style={{ "--d": i % SPAN_CYCLE.length }}
              className={`g-tile relative overflow-hidden border border-[var(--c-border)] bg-[var(--c-bg-elev)] group active:scale-[0.98] transition-transform ${SPAN_CYCLE[i % SPAN_CYCLE.length]}`}
              data-testid={`gallery-tile-${i}`}
            >
              <img
                src={img.src}
                alt={img.caption || img.ship}
                loading="lazy"
                className="archival-img w-full h-full object-cover"
              />
              {img.isDad && (
                <span
                  className="dad-badge absolute top-2 left-2 z-10"
                  data-testid={`gallery-tile-${i}-dad-badge`}
                >
                  Dad's photo
                </span>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--c-bg)]/90 via-[var(--c-bg)]/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-left">
                <p className="overline text-[10px] md:text-xs truncate">
                  {img.dates}
                </p>
                <p className="font-serif-display text-base sm:text-sm md:text-lg text-[var(--c-text)] mt-1 leading-tight truncate">
                  {img.ship}
                  {img.pennant && img.pennant !== "Shore Establishment" && (
                    <span className="text-[var(--c-accent)] italic ml-2 text-xs md:text-sm">
                      {img.pennant}
                    </span>
                  )}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={openIdx !== null} onOpenChange={(v) => !v && setOpenIdx(null)}>
        <DialogContent
          className="w-[96vw] max-w-5xl max-h-[92vh] bg-[var(--c-bg)] border border-[var(--c-border)] p-0 overflow-hidden flex flex-col"
          data-testid="gallery-dialog"
        >
          {open && (
            <>
              <DialogTitle className="sr-only">
                {open.ship} — photograph {openIdx + 1} of {images.length}
              </DialogTitle>
              <div
                ref={swipeRef}
                className="relative bg-[var(--c-bg)] flex-1 min-h-0 flex items-center justify-center select-none touch-pan-y p-3 sm:p-4"
              >
                <img
                  src={open.src}
                  alt={open.caption || open.ship}
                  draggable={false}
                  className="max-w-full max-h-full w-auto h-auto object-contain pointer-events-none"
                />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => go(-1)}
                      className="lb-arrow lb-arrow-left"
                      aria-label="Previous photograph"
                      data-testid="gallery-prev-btn"
                    >
                      <ChevronLeft size={22} />
                    </button>
                    <button
                      onClick={() => go(1)}
                      className="lb-arrow lb-arrow-right"
                      aria-label="Next photograph"
                      data-testid="gallery-next-btn"
                    >
                      <ChevronRight size={22} />
                    </button>
                  </>
                )}
              </div>
              <p className="text-center text-[10px] font-mono-plex tracking-widest text-[var(--c-text-muted)] pt-2 sm:hidden">
                ‹ swipe to browse ›
              </p>
              <div className="p-4 sm:p-6 md:p-8 border-t border-[var(--c-border)]">
                <p className="overline" data-testid="gallery-lightbox-counter">
                  {open.dates} · Plate {openIdx + 1} / {images.length}
                </p>
                <h3 className="font-serif-display text-xl sm:text-2xl md:text-3xl text-[var(--c-text)] mt-2">
                  {open.ship}
                  {open.pennant && open.pennant !== "Shore Establishment" && (
                    <span className="text-[var(--c-accent)] italic ml-3 text-base sm:text-lg md:text-xl">
                      {open.pennant}
                    </span>
                  )}
                </h3>
                {open.caption && (
                  <p className="text-[var(--c-text-muted)] mt-2 sm:mt-3 text-sm md:text-base max-w-2xl">
                    {open.caption}
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function FilterChip({ active, onClick, label, count, gold, testid }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      data-testid={testid}
      className={`glass-chip inline-flex items-center gap-2 border px-3.5 py-2 font-mono-plex text-[10px] sm:text-[11px] uppercase tracking-[0.16em] transition-colors min-h-[38px] ${
        active
          ? "border-[var(--c-accent)] bg-[var(--c-accent)]/15 text-[var(--c-text)]"
          : "border-[var(--c-border)] text-[var(--c-text-muted)] hover:border-[var(--c-accent)]/60 hover:text-[var(--c-text)]"
      }`}
    >
      {gold && (
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--c-accent)]" />
      )}
      {label}
      <span className="text-[var(--c-text-muted)] text-[9px]">{count}</span>
    </button>
  );
}
